import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";
import { getAffiliateRef, clearAffiliateRef } from "../hooks/useAffiliateRef";

const AuthContext = createContext(null);

// REACT_APP_BACKEND_URL must point to the FastAPI backend (e.g.
// https://api.hostprotect.fr). The front-end calls it directly — no proxy —
// so the deploy is portable across hosting platforms.
const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// ---- Token storage ----
// Mobile Safari blocks third-party cookies (Netlify ↔ Railway), so we also
// keep the JWT in localStorage and send it via the Authorization header. The
// backend accepts EITHER cookie OR Bearer header, so desktop browsers can
// keep using cookies without breaking.
const ACCESS_KEY = "hp_at";
const REFRESH_KEY = "hp_rt";
let _csrfToken = null;

export function setCsrfToken(token) {
  _csrfToken = token;
}

export function getCsrfToken() {
  return _csrfToken;
}

function setTokens(access, refresh) {
  try {
    if (access) localStorage.setItem(ACCESS_KEY, access);
    if (refresh) localStorage.setItem(REFRESH_KEY, refresh);
  } catch (_) {
    // localStorage unavailable (Safari private mode, etc.) — fall back to cookies
  }
}

function clearTokens() {
  try {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
  } catch (_) {}
}

function getAccessToken() {
  try {
    return localStorage.getItem(ACCESS_KEY);
  } catch (_) {
    return null;
  }
}

function getRefreshToken() {
  try {
    return localStorage.getItem(REFRESH_KEY);
  } catch (_) {
    return null;
  }
}

const UNSAFE_METHODS = new Set(["post", "put", "patch", "delete"]);

axios.interceptors.request.use((config) => {
  config.headers = config.headers || {};
  // Bearer header for the JWT (mobile-safe)
  const at = getAccessToken();
  if (at && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${at}`;
  }
  // CSRF (only meaningful when the backend reads the cookie auth path)
  const method = (config.method || "get").toLowerCase();
  if (UNSAFE_METHODS.has(method)) {
    const token = getCsrfToken();
    if (token) {
      config.headers["X-CSRF-Token"] = token;
    }
  }
  return config;
});

// If a request fails with 403 CSRF, fetch a fresh token once and retry
let csrfRetryInFlight = null;
axios.interceptors.response.use(
  (r) => r,
  async (error) => {
    const status = error.response?.status;
    const detail = error.response?.data?.detail;
    const original = error.config;
    if (
      status === 403 &&
      typeof detail === "string" &&
      detail.toLowerCase().includes("csrf") &&
      original &&
      !original.__csrfRetried
    ) {
      original.__csrfRetried = true;
      try {
        if (!csrfRetryInFlight) {
          csrfRetryInFlight = axios.get(`${API_URL}/api/auth/csrf`, { withCredentials: true });
        }
        const { data } = await csrfRetryInFlight;
        if (data?.csrf_token) setCsrfToken(data.csrf_token);
      } catch (_) {
        // ignore — user probably not logged in
      } finally {
        csrfRetryInFlight = null;
      }
      return axios(original);
    }
    return Promise.reject(error);
  }
);

// Helper to format API error details
function formatApiErrorDetail(detail) {
  if (detail == null) return "Une erreur est survenue. Veuillez réessayer.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check authentication on mount — also fetch CSRF token. If the access
  // token is expired, try to refresh once before giving up.
  useEffect(() => {
    const fetchMe = () => axios.get(`${API_URL}/api/auth/me`, { withCredentials: true });
    const tryRefresh = async () => {
      const rt = getRefreshToken();
      const body = rt ? { refresh_token: rt } : {};
      const { data } = await axios.post(`${API_URL}/api/auth/refresh`, body, { withCredentials: true });
      if (data?.csrf_token) setCsrfToken(data.csrf_token);
      setTokens(data.access_token, data.refresh_token);
    };
    const checkAuth = async () => {
      try {
        let resp;
        try {
          resp = await fetchMe();
        } catch (err) {
          if (err.response?.status === 401) {
            await tryRefresh();
            resp = await fetchMe();
          } else {
            throw err;
          }
        }
        setUser(resp.data);
        try {
          const { data: csrfData } = await axios.get(`${API_URL}/api/auth/csrf`, { withCredentials: true });
          if (csrfData?.csrf_token) setCsrfToken(csrfData.csrf_token);
        } catch (_) {}
      } catch (error) {
        clearTokens();
        setCsrfToken(null);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    checkAuth();
  }, []);

  const login = async (email, password, totpCode) => {
    try {
      const payload = { email, password };
      if (totpCode) payload.totp_code = totpCode;
      const { data } = await axios.post(
        `${API_URL}/api/auth/login`,
        payload,
        { withCredentials: true }
      );
      if (data.csrf_token) setCsrfToken(data.csrf_token);
      setTokens(data.access_token, data.refresh_token);
      setUser(data);
      return { success: true };
    } catch (error) {
      const message = formatApiErrorDetail(error.response?.data?.detail) || error.message;
      const requireTotp = error.response?.headers?.["x-require-totp"] === "1";
      return { success: false, error: message, requireTotp };
    }
  };

  const register = async (email, password, name, userType, country, legalStatus) => {
    try {
      const affiliateRef = getAffiliateRef();
      const payload = { email, password, name, user_type: userType, country, legal_status: legalStatus };
      if (affiliateRef) payload.affiliate_ref = affiliateRef;
      const { data } = await axios.post(
        `${API_URL}/api/auth/register`,
        payload,
        { withCredentials: true }
      );
      if (data.csrf_token) setCsrfToken(data.csrf_token);
      setTokens(data.access_token, data.refresh_token);
      if (affiliateRef) clearAffiliateRef();
      setUser(data);
      return { success: true };
    } catch (error) {
      const message = formatApiErrorDetail(error.response?.data?.detail) || error.message;
      return { success: false, error: message };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      setCsrfToken(null);
      clearTokens();
      setUser(null);
    }
  };

  const forgotPassword = async (email) => {
    try {
      const { data } = await axios.post(
        `${API_URL}/api/auth/forgot-password`,
        { email },
        { withCredentials: true }
      );
      return { success: true, message: data.message };
    } catch (error) {
      const message = formatApiErrorDetail(error.response?.data?.detail) || error.message;
      return { success: false, error: message };
    }
  };

  const resetPassword = async (token, newPassword) => {
    try {
      const { data } = await axios.post(
        `${API_URL}/api/auth/reset-password`,
        { token, new_password: newPassword },
        { withCredentials: true }
      );
      return { success: true, message: data.message };
    } catch (error) {
      const message = formatApiErrorDetail(error.response?.data?.detail) || error.message;
      return { success: false, error: message };
    }
  };

  const refreshToken = async () => {
    try {
      const rt = getRefreshToken();
      const body = rt ? { refresh_token: rt } : {};
      const { data } = await axios.post(`${API_URL}/api/auth/refresh`, body, { withCredentials: true });
      if (data?.csrf_token) setCsrfToken(data.csrf_token);
      setTokens(data.access_token, data.refresh_token);
      return true;
    } catch (error) {
      setCsrfToken(null);
      clearTokens();
      setUser(null);
      return false;
    }
  };

  const updateProfile = async (userType) => {
    try {
      await axios.put(
        `${API_URL}/api/auth/profile`,
        { user_type: userType },
        { withCredentials: true }
      );
      setUser((prev) => ({ ...prev, user_type: userType }));
      return { success: true };
    } catch (error) {
      const message = formatApiErrorDetail(error.response?.data?.detail) || error.message;
      return { success: false, error: message };
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        register,
        logout,
        forgotPassword,
        resetPassword,
        refreshToken,
        updateProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
